import type { RouteRecord } from 'vite-react-ssg';
import { Navigate, useLocation } from 'react-router-dom';
import { translatePathToEn } from './config/pathMap';
import Index from './pages/Index';
import RootLayout from './layouts/RootLayout';
import ProductDetail from './pages/ProductDetail';
import IndustryDetail from './pages/IndustryDetail';
import SolutionDetail from './pages/SolutionDetail';
import Preise from './pages/Preise';
import Integrationen from './pages/Integrationen';
import CaseStudyDetail from './pages/CaseStudyDetail';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import PartnerProgram from './pages/PartnerProgram';
import AboutUs from './pages/AboutUs';
import FAQ from './pages/FAQ';
import Careers from './pages/Careers';
import JobDetail from './pages/JobDetail';
import Nutzungsbedingungen from './pages/Nutzungsbedingungen';
import Datenschutz from './pages/Datenschutz';
import Impressum from './pages/Impressum';
import Kontakt from './pages/Kontakt';
import NotFound from './pages/NotFound';

/** Redirects old /en/german-slug paths to /en/english-slug */
const EnRedirect = () => {
  const { pathname } = useLocation();
  const withoutPrefix = pathname.slice(3);
  const translated = translatePathToEn(withoutPrefix);
  return <Navigate to={`/en${translated}`} replace />;
};

// Static path lists for SSG pre-rendering.
// vite-react-ssg expects full paths for dynamic routes so each page gets its own
// prerendered HTML head instead of falling back to the homepage head.
const productSlugs = [
  'ai-agent-plattform', 'ai-agent', 'ai-knowledge-base',
  'ai-automation-workflows', 'unified-inbox', 'analytics', 'enterprise-hosting',
];
const industrySlugs = [
  'maschinenbau', 'versicherung', 'einzelhandel',
  'it-software', 'e-commerce', 'tourismus', 'verbaende',
];
const solutionSlugs = ['marketing', 'vertrieb', 'kundenservice', 'ecommerce'];
const blogSlugs = [
  'whatsapp-shop-integration-big-green-egg',
  'ai-agent-kundenservice-automatisieren',
  'whatsapp-marketing-kampagnen',
];
const careerSlugs = ['saas-sales-specialist', 'enterprise-account-executive'];

const productPathsDe = productSlugs.map((slug) => `/produkte/${slug}`);
const productPathsEn = productSlugs.map((slug) => `/en/products/${slug}`);
const industryPathsDe = industrySlugs.map((slug) => `/branchen/${slug}`);
const industryPathsEn = industrySlugs.map((slug) => `/en/industries/${slug}`);
const solutionPathsDe = solutionSlugs.map((slug) => `/loesungen/${slug}`);
const solutionPathsEn = solutionSlugs.map((slug) => `/en/solutions/${slug}`);
const blogPathsDe = [...blogSlugs, 'top-10-ai-agent-plattformen-2026', 'botfriends-alternative', 'chatbase-alternative', 'fin-intercom-alternative', 'kauz-ai-alternative', 'lime-connect-alternative', 'melibo-alternative', 'moin-ai-alternative', 'parloa-alternative', 'superchat-alternative'].map((slug) => `/blog/${slug}`);
const blogPathsEn = blogSlugs.map((slug) => `/en/blog/${slug}`);
const careerPathsDe = careerSlugs.map((slug) => `/karriere/${slug}`);
const careerPathsEn = careerSlugs.map((slug) => `/en/careers/${slug}`);

export const routes: RouteRecord[] = [
  {
    element: <RootLayout />,
    children: [
      // ── German (default) routes ──
      { index: true, element: <Index /> },
      { path: 'produkte/:slug', element: <ProductDetail />, getStaticPaths: () => productPathsDe },
      { path: 'branchen/:slug', element: <IndustryDetail />, getStaticPaths: () => industryPathsDe },
      { path: 'loesungen/:slug', element: <SolutionDetail />, getStaticPaths: () => solutionPathsDe },
      { path: 'preise', element: <Preise /> },
      { path: 'integrationen', element: <Integrationen /> },
      { path: 'case-studies/online-shops-e-commerce/big-green-egg-head', element: <CaseStudyDetail /> },
      { path: 'case-studies/verbaende/customer-support', element: <CaseStudyDetail /> },
      { path: 'case-studies/marketing', element: <CaseStudyDetail /> },
      { path: 'case-studies/vertrieb', element: <CaseStudyDetail /> },
      { path: 'case-studies/*', element: <CaseStudyDetail /> },
      { path: 'blog', element: <Blog /> },
      { path: 'blog/:slug', element: <BlogPost />, getStaticPaths: () => blogPathsDe },
      { path: 'loesungspartner-programm', element: <PartnerProgram /> },
      { path: 'solution-partner', element: <PartnerProgram /> },
      { path: 'ueber-uns', element: <AboutUs /> },
      { path: 'faq', element: <FAQ /> },
      { path: 'karriere', element: <Careers /> },
      { path: 'karriere/:slug', element: <JobDetail />, getStaticPaths: () => careerPathsDe },
      { path: 'nutzungsbedingungen', element: <Nutzungsbedingungen /> },
      { path: 'datenschutzrichtlinie', element: <Datenschutz /> },
      { path: 'impressum', element: <Impressum /> },
      { path: 'kontakt', element: <Kontakt /> },

      // ── English routes ──
      { path: 'en', element: <Index /> },
      { path: 'en/products/:slug', element: <ProductDetail />, getStaticPaths: () => productPathsEn },
      { path: 'en/industries/:slug', element: <IndustryDetail />, getStaticPaths: () => industryPathsEn },
      { path: 'en/solutions/:slug', element: <SolutionDetail />, getStaticPaths: () => solutionPathsEn },
      { path: 'en/pricing', element: <Preise /> },
      { path: 'en/integrations', element: <Integrationen /> },
      { path: 'en/case-studies/online-shops-e-commerce/big-green-egg-head', element: <CaseStudyDetail /> },
      { path: 'en/case-studies/verbaende/customer-support', element: <CaseStudyDetail /> },
      { path: 'en/case-studies/marketing', element: <CaseStudyDetail /> },
      { path: 'en/case-studies/vertrieb', element: <CaseStudyDetail /> },
      { path: 'en/case-studies/*', element: <CaseStudyDetail /> },
      { path: 'en/blog', element: <Blog /> },
      { path: 'en/blog/:slug', element: <BlogPost />, getStaticPaths: () => blogPathsEn },
      { path: 'en/solution-partner', element: <PartnerProgram /> },
      { path: 'en/about', element: <AboutUs /> },
      { path: 'en/faq', element: <FAQ /> },
      { path: 'en/careers', element: <Careers /> },
      { path: 'en/careers/:slug', element: <JobDetail />, getStaticPaths: () => careerPathsEn },
      { path: 'en/terms', element: <Nutzungsbedingungen /> },
      { path: 'en/privacy', element: <Datenschutz /> },
      { path: 'en/imprint', element: <Impressum /> },
      { path: 'en/contact', element: <Kontakt /> },

      // ── EN redirects: old German slugs → English slugs ──
      { path: 'en/produkte/:slug', element: <EnRedirect /> },
      { path: 'en/branchen/:slug', element: <EnRedirect /> },
      { path: 'en/loesungen/:slug', element: <EnRedirect /> },
      { path: 'en/preise', element: <Navigate to="/en/pricing" replace /> },
      { path: 'en/integrationen', element: <Navigate to="/en/integrations" replace /> },
      { path: 'en/loesungspartner-programm', element: <Navigate to="/en/solution-partner" replace /> },
      { path: 'en/ueber-uns', element: <Navigate to="/en/about" replace /> },
      { path: 'en/karriere', element: <Navigate to="/en/careers" replace /> },
      { path: 'en/karriere/:slug', element: <EnRedirect /> },
      { path: 'en/nutzungsbedingungen', element: <Navigate to="/en/terms" replace /> },
      { path: 'en/datenschutzrichtlinie', element: <Navigate to="/en/privacy" replace /> },
      { path: 'en/impressum', element: <Navigate to="/en/imprint" replace /> },
      { path: 'en/kontakt', element: <Navigate to="/en/contact" replace /> },

      // ── Legacy redirects ──
      { path: 'produkte/enterprise-hosting/kontakt', element: <Navigate to="/kontakt" replace /> },
      { path: 'loesungen/on-premise/kontakt', element: <Navigate to="/kontakt" replace /> },
      { path: 'loesungen/on-premise', element: <Navigate to="/produkte/enterprise-hosting" replace /> },

      // ── 404 catch-all ──
      { path: '*', element: <NotFound /> },
    ],
  },
];
